// @ts-check
/**
 * Data loading and mutations for the Triggers tab: the four triggers plus the approved templates
 * they can be pointed at, loaded together with a ticket guard, and the two write paths (save the
 * template/mapping, flip enabled on or off) each own their success/failure/toast behaviour so
 * TriggerCard stays presentational.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../../context/ToastContext';
import adminWhatsAppService from '../../services/adminWhatsAppService';
import { WHATSAPP_TEMPLATE_STATUS } from '../../utils/whatsapp';

export function useTriggers() {
  const toast = useToast();
  const [triggers, setTriggers] = useState(/** @type {import('../../services/adminWhatsAppService').Trigger[] | null} */ (null));
  const [templates, setTemplates] = useState(/** @type {import('../../services/adminWhatsAppService').WhatsAppTemplate[] | null} */ (null));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const latest = useRef(0);

  useEffect(
    () => () => {
      latest.current += 1;
    },
    [],
  );

  const load = useCallback(async () => {
    const ticket = ++latest.current;
    setLoading(true);
    try {
      const [triggerList, templateList] = await Promise.all([
        adminWhatsAppService.listTriggers(),
        adminWhatsAppService.listTemplates({ status: WHATSAPP_TEMPLATE_STATUS.APPROVED }),
      ]);
      if (ticket !== latest.current) return;
      setTriggers(triggerList);
      setTemplates(templateList);
      setError('');
    } catch (err) {
      if (ticket !== latest.current) return;
      setError(err.message);
    } finally {
      if (ticket === latest.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const replaceTrigger = useCallback((key, next) => {
    setTriggers((prev) => (prev ? prev.map((t) => (t.key === key ? next : t)) : prev));
  }, []);

  /** Save the template/mapping only (never `enabled`). Leaves the card's own fields untouched on failure. */
  const saveTrigger = useCallback(
    async (key, patch) => {
      try {
        const updated = await adminWhatsAppService.updateTrigger(key, patch);
        replaceTrigger(key, updated);
        toast.success(`${updated.label} saved.`);
      } catch (err) {
        toast.error(err.message);
      }
    },
    [replaceTrigger, toast],
  );

  /**
   * Enabling never flips the switch before the server confirms it (no optimism); disabling always
   * succeeds per the backend rule, so it flips immediately and only rolls back on a real failure.
   */
  const setEnabled = useCallback(
    async (trigger, enabled) => {
      const key = trigger.key;
      if (!enabled) {
        replaceTrigger(key, { ...trigger, enabled: false });
        try {
          const updated = await adminWhatsAppService.updateTrigger(key, { enabled: false });
          replaceTrigger(key, updated);
        } catch (err) {
          replaceTrigger(key, { ...trigger, enabled: true });
          toast.error(err.message);
        }
        return;
      }
      try {
        const updated = await adminWhatsAppService.updateTrigger(key, { enabled: true });
        replaceTrigger(key, updated);
      } catch (err) {
        toast.error(err.message);
      }
    },
    [replaceTrigger, toast],
  );

  return { triggers, templates, loading, error, reload: load, saveTrigger, setEnabled };
}
